import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Typography, useTheme } from '@mui/material';
import { Button } from '@components/buttons/Button';
import { fonts } from '@theme/fontsCustomer';
import { BulletIcon } from '@icons/BulletIcon';
import { useGetScimTokens } from '@queryHooks/useScimToken';
import { ScimTokenCreateModal } from './ScimTokenCreateModal';
import { ScimTokenTable } from './ScimTokenTable';

export const ScimTokenPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const tenantId = params.tenantId as string;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: tokens, isLoading } = useGetScimTokens(tenantId);

  return (
    <Container>
      <PageTitle>{t('ScimToken.title')}</PageTitle>

      <ContentArea>
        <InfoBox>
          <InfoItem>
            <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
            <InfoText>{t('ScimToken.info')}</InfoText>
          </InfoItem>
        </InfoBox>

        <TableHeader>
          <Button variant="contain" size="small" paddingHorizontal={16} onClick={() => setIsCreateModalOpen(true)}>
            {t('ScimToken.button.create')}
          </Button>
        </TableHeader>

        <ScimTokenTable tokens={tokens ?? []} isLoading={isLoading} tenantId={tenantId} />
      </ContentArea>

      <ScimTokenCreateModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} tenantId={tenantId} />
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  padding: 42px 32px 32px 32px;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
`;

const PageTitle = styled(Typography)`
  ${fonts.Headline5}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  margin-bottom: 28px;
` as typeof Typography;

const ContentArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 20px 32px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 3px;
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const TableHeader = styled(Box)`
  display: flex;
  justify-content: flex-end;
`;
